/* =============================================================================
   paw-upload-capture.js — CUSTOM (pawarts) · Lever L3 (spec 03_specs/14)
   REVISED 2026-07-11: capture the PHOTO-UPLOAD event itself (asking for an email at
   the upload step felt unnatural). No email is collected here.

   On file selection in a Globo upload dropzone (wrapper class `.photoUpload`), fire a
   `Started Custom Order` event — to Klaviyo (anonymous, no identify) AND to EdgeTag →
   Facebook/Google — carrying the displayed variant's details (variant_name, variant_sku,
   product_type, image_url, value). It then proactively OPENS the Klaviyo email-capture form a
   few seconds later (openForm) to capture the email in the same session; Klaviyo merges the
   anonymous profile → identified profile, and the recovery flow then sends.

   Variant details come from a Liquid data blob `#paw-product-data` rendered in
   sections/main-product.liquid (see DEPLOY) — null-safe if absent.

   Fail-safe: never blocks the upload / ATC, never throws, fires once per pageview.
   API verified web-scout 2026-07-11 (Klaviyo track; EdgeTag tag).

   ⚠️ Klaviyo flow dependency (config, not code): the "Abandoned Custom Order" flow must
   trigger on `Started Custom Order` and have an initial delay (E1 ~1h) so the visitor has
   time to enter their email in the (Klaviyo-native) popup and be merged before the first
   send. See DEPLOY note.
   ============================================================================= */
(function () {
  'use strict';
  if (window.__pawUploadCaptureBound) return;
  window.__pawUploadCaptureBound = true;

  // ---- CONFIG ----------------------------------------------------------------
  // The photo-upload file input lives inside the operator-set wrapper class `photoUpload`.
  var KLAVIYO_METRIC = 'Started Custom Order';
  // Also send the same event to EdgeTag → Facebook/Google (event-only, NO email/advanced
  // matching at this step). Event name kept identical to Klaviyo for cross-platform consistency.
  // ⚠️ EdgeTag routes STANDARD event names to Meta automatically; a CUSTOM name like this may need
  // to be mapped/enabled as a custom event in the Blotout/EdgeTag dashboard to forward to FB/Google.
  var FIRE_EDGETAG = true;
  var EDGETAG_EVENT = 'Started Custom Order';
  // After an upload, open the Klaviyo email-capture form (email capture) a short while later, so the
  // visitor gives their email → Klaviyo identifies → the anonymous `Started Custom Order` event
  // merges onto their profile → the recovery flow can send. Klaviyo's documented `openForm` trigger.
  var OPEN_SPINWHEEL = true;
  var SPINWHEEL_FORM_ID = 'XqX7jy';   // "Photo upload" email-capture form (custom-trigger only)
  var OPENFORM_DELAY_MS = 3000;        // 3s: Globo has drawn the thumbnail, intent still hot
  var SKIP_IF_KNOWN = true;            // don't pop the form at an already-identified visitor

  var fired = false;      // upload = a single high-intent signal; once per pageview is enough
  var formOpened = false;   // open the capture form at most once per pageview
  var edgetagFired = false; // EdgeTag needs its own latch: a Klaviyo retry must not double-send

  function isUploadField(el) {
    if (!el || el.tagName !== 'INPUT' || (el.getAttribute('type') || '').toLowerCase() !== 'file') return false;
    if (!el.closest) return false;
    // Require the operator-set `.photoUpload` wrapper (F2: avoids other Globo file inputs,
    // e.g. the Form Builder survey, which also carry `.gpo-dropzone__input`).
    return !!el.closest('.photoUpload');
  }

  // Liquid-rendered variant data blob (#paw-product-data), keyed by variant id → { name, sku, image }.
  // Rendered in sections/main-product.liquid (see DEPLOY). Null-safe if absent.
  function readProductData() {
    try {
      var el = document.getElementById('paw-product-data');
      return el ? JSON.parse(el.textContent) : null;
    } catch (e) { return null; }
  }

  function productContext() {
    var ctx = { currency: 'USD', url: location.href };
    try {
      var og = document.querySelector('meta[property="og:title"]');
      ctx.content_name = (og && og.content) || document.title;

      // Currently-selected variant id (updated by the theme's variant selectors).
      var idInput = document.querySelector('form[action*="/cart/add"] [name="id"]');
      var vid = idInput && idInput.value ? String(idInput.value) : null;
      if (vid) ctx.variant_id = vid;

      // Details of the variant displayed at upload time, from the Liquid data blob.
      var data = readProductData();
      if (data) {
        if (data.type) ctx.product_type = data.type;               // e.g. "Classic Tee"
        var v = (vid && data.variants) ? data.variants[vid] : null;
        if (v) {
          if (v.name) ctx.variant_name = v.name;                   // e.g. "Leaf / XL"
          if (v.sku) ctx.variant_sku = v.sku;
          if (v.image) ctx.image_url = v.image.indexOf('//') === 0 ? 'https:' + v.image : v.image;
        }
      }

      var priceEl = document.querySelector(
        '[data-product-price], .price-item--regular, .price .amount'
      );
      if (priceEl) {
        var m = (priceEl.textContent || '').match(/\d+(?:[.,]\d{2})?/);
        if (m) ctx.value = parseFloat(m[0].replace(',', '.'));
      }
    } catch (e) {}
    return ctx;
  }

  function fire() {
    if (fired) return;
    var ctx = productContext();
    var klaviyoSent = false;

    // Klaviyo — anonymous track (NO identify). Klaviyo stores it client-side and backfills
    // it onto the profile once the email-capture form popup identifies the visitor (same cookie).
    try {
      if (window.klaviyo && typeof window.klaviyo.track === 'function') {
        window.klaviyo.track(KLAVIYO_METRIC, ctx);
        klaviyoSent = true;
      } else if (window._learnq && typeof window._learnq.push === 'function') {
        window._learnq.push(['track', KLAVIYO_METRIC, ctx]);
        klaviyoSent = true;
      }
    } catch (e) {}

    // EdgeTag → Facebook/Google (event-only, no email → no advanced matching). Bonus, not
    // the primary goal, so it does NOT gate the dedupe (F1). FB-friendly payload.
    try {
      if (FIRE_EDGETAG && !edgetagFired && typeof window.edgetag === 'function') {
        edgetagFired = true;
        window.edgetag('tag', EDGETAG_EVENT, {
          content_name: ctx.content_name,
          content_type: 'product',
          content_ids: ctx.variant_sku ? [ctx.variant_sku] : (ctx.variant_id ? [ctx.variant_id] : undefined),
          content_category: ctx.product_type,
          value: ctx.value,
          currency: ctx.currency
        });
      }
    } catch (e) {}

    // F1: only the PRIMARY Klaviyo signal locks the dedupe. If Klaviyo wasn't ready yet,
    // leave `fired` false so a later change/re-pick retries (EdgeTag success never latches).
    if (klaviyoSent) fired = true;
  }

  // Open the Klaviyo email-capture form once, a short delay after the upload (email capture).
  function maybeOpenForm() {
    if (formOpened || !OPEN_SPINWHEEL || !SPINWHEEL_FORM_ID) return;
    formOpened = true;

    function open() {
      try {
        window._klOnsite = window._klOnsite || [];
        window._klOnsite.push(['openForm', SPINWHEEL_FORM_ID]);
      } catch (e) {}
    }

    setTimeout(function () {
      // Don't pop the form at someone who already gave us their email.
      if (!SKIP_IF_KNOWN) { open(); return; }
      try {
        if (window.klaviyo && typeof window.klaviyo.isIdentified === 'function') {
          window.klaviyo.isIdentified().then(function (known) {
            if (!known) open();
          })['catch'](open);
          return;
        }
      } catch (e) {}
      open();
    }, OPENFORM_DELAY_MS);
  }

  function onChange(e) {
    // Never throw out of a global capture-phase listener.
    try {
      var el = e.target;
      if (!isUploadField(el)) return;
      if (el.files && el.files.length > 0) {
        fire();          // Klaviyo/EdgeTag `Started Custom Order`
        maybeOpenForm(); // then nudge the email-capture form popup to capture the email
      }
    } catch (_) {}
  }

  // File inputs fire `change` on selection; `change` bubbles, but use capture to be safe
  // against stopPropagation inside Globo's dropzone handlers.
  document.addEventListener('change', onChange, true);
})();
